import { useTenantStore } from '@/stores/tenantStore'
import { EnumFromUnion } from '@/utils/types'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { API_BASE_URL } from '../config'
import { InferRequest, InferResponse, fetchClient } from '../typedClient'

export type SystemInstance = InferResponse<'/meta/tenant/instance/upsert'>
export type SystemInstanceUpsert = Partial<
  InferRequest<'/meta/tenant/instance/upsert'>
>
export type Partner = InferResponse<'/meta/tenant/partner/upsert'>
export type TankReadingConfig =
  InferResponse<'/meta/tenant/tank-reading-config/upsert'>
export type TankReadingConfigUpsert = Partial<
  InferRequest<'/meta/tenant/tank-reading-config/upsert'>
>
export type PartnerScope = Partner['scopes'][number]
export type TenantConfig = Awaited<ReturnType<typeof tenantConfigApi.getConfig>>
export type ChangeRuleTypeMetadata = InferResponse<
  '/meta/tenant/change-rule-types',
  'get'
>[number]
export type NotificationTypeMetadata = InferResponse<
  '/meta/tenant/notification-types',
  'get'
>[number]
export type NotificationConfig = NonNullable<
  SystemInstance['notification_config']
>
export type SourcingStrategyType = NonNullable<
  Partner['sourcing_config']
>['strategy']
export type PriceTypeType = NonNullable<
  NonNullable<Partner['sourcing_config']>['default_price_type']
>
export type DefaultSupplyPriceType = NonNullable<
  NonNullable<Partner['sourcing_config']>['default_supply_price']
>

export type TestCredentialsResult =
  InferResponse<'/meta/tenant/instance/test-credentials'>
export type SDScopeStatus = TestCredentialsResult['scopes'][number]

// Runtime enum for SourcingStrategy. This should error if new strategies are added server-side without updating the frontend.
export const SourcingStrategy = {
  SPECIFIC_SUPPLY: 'Specific Supply',
  TANK_SUPPLY_DEFAULT: 'Tank Supply Default',
  USE_BEST: 'Use Best',
  MANUAL: 'Manual',
  MAINTAIN: 'Maintain',
} as const satisfies EnumFromUnion<SourcingStrategyType>

// Runtime enum for PriceType. This should error if new price types are added server-side without updating the frontend.
export const PriceType = {
  RACK: 'rack',
  CONTRACT: 'contract',
  INDEX: 'index',
  INVENTORY: 'inventory',
  SPOT: 'spot',
} as const satisfies EnumFromUnion<PriceTypeType>

// Runtime enum for DefaultSupplyPrice. The server-side values are the literal
// price strings, so these keys carry the min/max meaning.
export const DefaultSupplyPrice = {
  MIN: '$0.00001',
  MAX: '$9.99999',
} as const satisfies Record<'MIN' | 'MAX', DefaultSupplyPriceType>

const tenantConfigApi = {
  getConfig: async (tenantName: string) => {
    const { data, error } = await fetchClient.GET('/meta/tenant/config', {
      params: { header: { 'X-Tenant-Name': tenantName } },
    })
    if (error) {
      throw new Error(`Failed to fetch tenant config: ${JSON.stringify(error)}`)
    }
    return data
  },

  getAvailableScopes: async (tenantName: string) => {
    const { data, error } = await fetchClient.GET('/meta/tenant/scopes', {
      params: { header: { 'X-Tenant-Name': tenantName } },
    })
    if (error) {
      throw new Error(
        `Failed to fetch available scopes: ${JSON.stringify(error)}`,
      )
    }
    return data as PartnerScope[] | undefined
  },

  getChangeRuleTypes: async (tenantName: string) => {
    const { data, error } = await fetchClient.GET(
      '/meta/tenant/change-rule-types',
      {
        params: { header: { 'X-Tenant-Name': tenantName } },
      },
    )
    if (error) {
      throw new Error(
        `Failed to fetch change rule types: ${JSON.stringify(error)}`,
      )
    }
    return data
  },

  getNotificationTypes: async (tenantName: string) => {
    const { data, error } = await fetchClient.GET(
      '/meta/tenant/notification-types',
      {
        params: { header: { 'X-Tenant-Name': tenantName } },
      },
    )
    if (error) {
      throw new Error(
        `Failed to fetch notification types: ${JSON.stringify(error)}`,
      )
    }
    return data
  },

  upsertInstance: async (
    tenantName: string,
    tenantId: string,
    instance: InferRequest<'/meta/tenant/instance/upsert'>,
  ): Promise<SystemInstance> => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/instance/upsert',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId },
        },
        body: instance as never,
      },
    )
    if (error) {
      throw new Error(`Failed to upsert instance: ${JSON.stringify(error)}`)
    }
    return data
  },

  deleteInstance: async (
    tenantName: string,
    tenantId: string,
    instanceId: string,
  ) => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/instance/delete',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId, instance_id: instanceId },
        },
      },
    )
    if (error) {
      throw new Error(`Failed to delete instance: ${JSON.stringify(error)}`)
    }
    return data
  },

  upsertPartner: async (
    tenantName: string,
    tenantId: string,
    partner: InferRequest<'/meta/tenant/partner/upsert'>,
  ) => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/partner/upsert',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId },
        },
        body: partner as never,
      },
    )
    if (error) {
      throw new Error(`Failed to upsert partner: ${JSON.stringify(error)}`)
    }
    return data
  },

  upsertTankReadingConfig: async (
    tenantName: string,
    tenantId: string,
    config: InferRequest<'/meta/tenant/tank-reading-config/upsert'>,
  ) => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/tank-reading-config/upsert',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId },
        },
        body: config as never,
      },
    )
    if (error) {
      throw new Error(
        `Failed to update reading sharing: ${JSON.stringify(error)}`,
      )
    }
    return data
  },

  deletePartner: async (
    tenantName: string,
    tenantId: string,
    partnerTenantId: string,
    partnerInstanceId: string,
  ) => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/partner/delete',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: {
            tenant_id: tenantId,
            partner_tenant_id: partnerTenantId,
            partner_instance_id: partnerInstanceId,
          } as never,
        },
      },
    )
    if (error) {
      throw new Error(`Failed to delete partner: ${JSON.stringify(error)}`)
    }
    return data
  },

  uploadLogo: async (
    tenantName: string,
    tenantId: string,
    file: File,
  ): Promise<{ success: boolean; logo_url: string | null }> => {
    const formData = new FormData()
    formData.append('file', file)

    const token = localStorage.getItem('accessToken')
    const response = await fetch(
      `${API_BASE_URL}/meta/tenant/logo/upload?tenant_id=${tenantId}`,
      {
        method: 'POST',
        headers: {
          'X-Tenant-Name': tenantName,
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      },
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.detail || 'Failed to upload logo')
    }

    return response.json()
  },

  deleteLogo: async (tenantName: string, tenantId: string) => {
    const { data, error } = await fetchClient.DELETE('/meta/tenant/logo', {
      params: {
        header: { 'X-Tenant-Name': tenantName },
        query: { tenant_id: tenantId },
      },
    })
    if (error) {
      throw new Error(`Failed to delete logo: ${JSON.stringify(error)}`)
    }
    return data
  },

  testInstanceCredentials: async (
    tenantName: string,
    tenantId: string,
    instanceId: string,
  ): Promise<TestCredentialsResult> => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/instance/test-credentials',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId, instance_id: instanceId },
        },
      },
    )
    if (error) {
      throw new Error(`Failed to test credentials: ${JSON.stringify(error)}`)
    }
    return data
  },

  /**
   * Test instance credentials using explicit tenant_id and instance_id.
   * Used from log details where the selected tenant may differ from the failing tenant.
   */
  testInstanceCredentialsDirect: async (
    tenantId: string,
    instanceId: string,
  ): Promise<TestCredentialsResult> => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/instance/test-credentials',
      {
        params: {
          header: { 'X-Tenant-Name': 'Gravitate' },
          query: { tenant_id: tenantId, instance_id: instanceId },
        },
      },
    )
    if (error) {
      throw new Error(`Failed to test credentials: ${JSON.stringify(error)}`)
    }
    return data
  },

  getPartnerInstances: async (
    tenantName: string,
    tenantId: string,
    partnerTenantId: string,
  ) => {
    const { data, error } = await fetchClient.POST(
      '/meta/tenant/get_partner_instances',
      {
        params: {
          header: { 'X-Tenant-Name': tenantName },
          query: { tenant_id: tenantId },
        },
        body: partnerTenantId as never,
      },
    )
    if (error) {
      throw new Error(
        `Failed to fetch partner instances: ${JSON.stringify(error)}`,
      )
    }
    return data
  },
}

// Hooks
export const useTenantConfig = () => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''

  return useQuery({
    queryKey: ['tenant-config', tenantName],
    queryFn: () => tenantConfigApi.getConfig(tenantName),
    enabled: !!tenantName,
  })
}

export const useAvailableScopes = () => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''

  return useQuery({
    queryKey: ['tenant-scopes', tenantName],
    queryFn: () => tenantConfigApi.getAvailableScopes(tenantName),
    enabled: !!tenantName,
  })
}

export const useChangeRuleTypes = () => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''

  return useQuery({
    queryKey: ['change-rule-types', tenantName],
    queryFn: () => tenantConfigApi.getChangeRuleTypes(tenantName),
    enabled: !!tenantName,
  })
}

export const useNotificationTypes = () => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''

  return useQuery({
    queryKey: ['notification-types', tenantName],
    queryFn: () => tenantConfigApi.getNotificationTypes(tenantName),
    enabled: !!tenantName,
  })
}

export const useUpsertInstance = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (instance: SystemInstanceUpsert) =>
      tenantConfigApi.upsertInstance(
        tenantName,
        tenantId,
        instance as InferRequest<'/meta/tenant/instance/upsert'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useDeleteInstance = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (instanceId: string) =>
      tenantConfigApi.deleteInstance(tenantName, tenantId, instanceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useUpsertPartner = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (
      partner: Partial<InferRequest<'/meta/tenant/partner/upsert'>>,
    ) =>
      tenantConfigApi.upsertPartner(
        tenantName,
        tenantId,
        partner as InferRequest<'/meta/tenant/partner/upsert'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useUpsertTankReadingConfig = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (config: TankReadingConfigUpsert) =>
      tenantConfigApi.upsertTankReadingConfig(
        tenantName,
        tenantId,
        config as InferRequest<'/meta/tenant/tank-reading-config/upsert'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useDeletePartner = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: ({
      partnerTenantId,
      partnerInstanceId,
    }: { partnerTenantId: string; partnerInstanceId: string }) =>
      tenantConfigApi.deletePartner(
        tenantName,
        tenantId,
        partnerTenantId,
        partnerInstanceId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export type PartnerInstance =
  InferResponse<'/meta/tenant/get_partner_instances'>[number]

export const useAllPartnerInstances = (partnerTenantIds: string[]) => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  const queries = useQueries({
    queries: partnerTenantIds.map((ptId) => ({
      queryKey: ['partner-instances', tenantName, ptId],
      queryFn: () =>
        tenantConfigApi.getPartnerInstances(tenantName, tenantId, ptId),
      enabled: !!tenantName && !!tenantId,
    })),
  })

  const instancesByPartner = Object.fromEntries(
    partnerTenantIds.map((ptId, i) => [ptId, queries[i]?.data || []]),
  )

  return instancesByPartner
}

export const usePartnerInstances = (partnerTenantId: string | undefined) => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useQuery({
    queryKey: ['partner-instances', tenantName, partnerTenantId],
    queryFn: () =>
      tenantConfigApi.getPartnerInstances(
        tenantName,
        tenantId,
        partnerTenantId!,
      ),
    enabled: !!tenantName && !!tenantId && !!partnerTenantId,
  })
}

export const useUploadTenantLogo = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (file: File) =>
      tenantConfigApi.uploadLogo(tenantName, tenantId, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useDeleteTenantLogo = () => {
  const queryClient = useQueryClient()
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: () => tenantConfigApi.deleteLogo(tenantName, tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantName] })
    },
  })
}

export const useTestInstanceCredentials = () => {
  const selectedTenant = useTenantStore().selectedTenant
  const tenantName = selectedTenant?.name || ''
  const tenantId = selectedTenant?.id || ''

  return useMutation({
    mutationFn: (instanceId: string) =>
      tenantConfigApi.testInstanceCredentials(tenantName, tenantId, instanceId),
  })
}

/** Test credentials using explicit tenant_id + instance_id (for log detail auth errors). */
export const useTestInstanceCredentialsDirect = () => {
  return useMutation({
    mutationFn: ({
      tenantId,
      instanceId,
    }: { tenantId: string; instanceId: string }) =>
      tenantConfigApi.testInstanceCredentialsDirect(tenantId, instanceId),
  })
}
